import React, { useState } from 'react';

import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SettingsIcon from '@mui/icons-material/Settings';
import { ListItem } from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import CheckAccess from 'common/components/CheckAccess';
import { useBreakpoint } from 'common/hooks';

import ConfigurationOptions from './ConfigurationOptions';
import useCompanyOptions from './hooks/useCompanyOptions';
import useIntegrationsOptions from './hooks/useIntegrationsOptions';
import useOperativeOptions from './hooks/useOperativeOptions';
import ResourcesOptions from './ResourcesOptions';
import SecondaryDrawer from './SecondaryDrawer';
import StyledList from './StyledComponents/StyledList';
import StyledListItemButton from './StyledComponents/StyledListItemButton';
import StyledListItemText from './StyledComponents/StyledListItemTextOption';

const renderOption = (props) => {
    const {
        drawerSize,
        Icon,
        iconId,
        listItemClassName,
        requiredPermissions,
        selected,
        setOpenDrawer,
        setSubMenu,
        Submenu,
        title,
    } = props;

    const breakpoint = useBreakpoint();
    const isMobile = ['xs', 'sm'].includes(breakpoint);

    const onClickOption = () => {
        setSubMenu(Submenu);
        setOpenDrawer(true);
    };

    return (
        <CheckAccess requiredPermissions={requiredPermissions}>
            <ListItem className={listItemClassName} disablePadding>
                <StyledListItemButton
                    drawersize={drawerSize}
                    id={iconId || title}
                    onClick={onClickOption}
                    selected={selected}
                >
                    {Icon && <Icon />}
                    <StyledListItemText primary={title} />
                    {isMobile && <NavigateNextIcon />}
                </StyledListItemButton>
            </ListItem>
        </CheckAccess>
    );
};

const OtherOptionsMenu = ({
    container,
    drawerSize,
    handleDrawerToggle,
    isOpenDrawer,
    mobileOpen,
    setMobileOpen,
    setOpenDrawer,
    setSidebarOpen,
    sidebarOpen,
}) => {
    const { t } = useTranslation();

    const [Submenu, setSubMenu] = useState(null);
    const companyOptions = useCompanyOptions();
    const integrationsOptions = useIntegrationsOptions();
    const operativeOptions = useOperativeOptions();

    const toggleOpenDrawer = () => setOpenDrawer((prev) => !prev);

    const handleClose = () => {
        setOpenDrawer(false);
        setSidebarOpen(false);
        setMobileOpen(false);
    };

    const closeSecondaryDrawer = () => {
        toggleOpenDrawer();
        setSidebarOpen(false);
    };

    const commonMenuProps = {
        container,
        drawerSize,
        handleDrawerToggle,
        mobileOpen,
        setMobileOpen,
        setOpenDrawer,
        setSidebarOpen,
        sidebarOpen,
    };

    const settingOptions = [
        ...companyOptions,
        ...integrationsOptions,
        ...operativeOptions,
    ];

    const requiredPermissions = settingOptions
        .map((option) => option.permission)
        .flat();

    const selected = settingOptions.some((option) => option.selected);

    return (
        <>
            <StyledList sx={{ marginTop: 'auto' }}>
                {renderOption({
                    ...commonMenuProps,
                    Icon: SettingsIcon,
                    iconId: 'settings-icon',
                    requiredPermissions,
                    selected,
                    Submenu: <ConfigurationOptions handleClose={handleClose} />,
                    title: t('Setting'),
                    setOpenDrawer,
                    setSubMenu,
                })}
                {renderOption({
                    ...commonMenuProps,
                    Icon: HelpOutlineIcon,
                    iconId: 'help-icon',
                    requiredPermissions,
                    selected,
                    Submenu: <ResourcesOptions handleClose={handleClose} />,
                    title: t('Resource', { count: 2 }),
                    setOpenDrawer,
                    setSubMenu,
                })}
            </StyledList>

            <SecondaryDrawer
                {...{
                    container,
                    drawerSize,
                    handleDrawerToggle,
                    mobileOpen: isOpenDrawer,
                    onClose: closeSecondaryDrawer,
                    setSidebarOpen,
                    sidebarOpen,
                }}
            >
                {Submenu}
            </SecondaryDrawer>
        </>
    );
};

OtherOptionsMenu.propTypes = {
    container: PropTypes.func,
    drawerSize: PropTypes.string,
    handleDrawerToggle: PropTypes.func,
    isOpenDrawer: PropTypes.bool,
    mobileOpen: PropTypes.bool,
    setMobileOpen: PropTypes.func,
    setOpenDrawer: PropTypes.func,
    setSidebarOpen: PropTypes.func,
    sidebarOpen: PropTypes.bool,
};

export default OtherOptionsMenu;
